import { el } from "../utils/dom";

/**
 * Status - never a badge/pill. A colored word in the UI font, sometimes with a
 * small dot. Tone is a presentation concept (not the raw backend string):
 * "success" | "danger" | "accent" (the one thing needing attention -
 * partial runs, degraded checks) | "muted" (unknown / not yet run).
 */
export type StatusTone = "success" | "danger" | "accent" | "muted";

export interface StatusTextOptions {
  dot?: boolean;
  pulse?: boolean;
}

export function renderStatusText(label: string, tone: StatusTone, options: StatusTextOptions = {}): HTMLElement {
  const { dot = false, pulse = false } = options;
  const children = [];
  if (dot) {
    children.push(el("span", { className: `status-text__dot${pulse ? " pulse" : ""}` }));
  }
  children.push(document.createTextNode(label));
  return el("span", { className: `status-text status-text--${tone}` }, children);
}

/** Map a sync run status to a presentation tone + label. */
export function runStatusTone(status: string | null | undefined): { label: string; tone: StatusTone; pulse: boolean } {
  switch (status) {
    case "success":
      return { label: "Success", tone: "success", pulse: false };
    case "partial":
      return { label: "Partial", tone: "accent", pulse: false };
    case "failed":
      return { label: "Failed", tone: "danger", pulse: false };
    case "running":
      return { label: "Running", tone: "accent", pulse: true };
    case "cancelled":
    case "canceled":
      return { label: "Cancelled", tone: "muted", pulse: false };
    default:
      if (status) return { label: status, tone: "muted", pulse: false };
      return { label: "Never run", tone: "muted", pulse: false };
  }
}

/** Map a health check value ("ok" | "failed") to a presentation tone + label. */
export function checkTone(value: string | null | undefined): { label: string; tone: StatusTone } {
  if (value === "ok") return { label: "OK", tone: "success" };
  if (value === "failed") return { label: "Failed", tone: "danger" };
  return { label: "Unknown", tone: "muted" };
}
